// components/WorkspaceVersionsPanel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { WorkspaceVersion, GenerationTask, Workspace } from '#types/types';
import { Check, Plus, Minus, ThumbsUp, ThumbsDown, Star, Eye, ScanEye } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import { buildLiveScreenSize, buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import { STUDIO_LIVE_VIEWPORT_VARS_RESET } from '#lib/studio-viewport';
import {
  computeLineageFromFocus,
  computeVersionHierarchyLabels,
  sortVersionsByRecency,
  sortVersionsByTimeline,
} from '#lib/workspace-version-labels';
import { resolveVersionThumbnailSize, VERSION_THUMBNAIL_MAX_SIDE } from '#lib/workspace-version-thumbnail';
import { STUDIO_MODEL_REGISTRY, resolveRegistryModelIdFromRuntimeName, type StudioModelId } from '#lib/model-registry';

const MemoizedCheck = React.memo(Check);
const MemoizedThumbsUp = React.memo(ThumbsUp);
const MemoizedThumbsDown = React.memo(ThumbsDown);
const MemoizedPlus = React.memo(Plus);
const MemoizedMinus = React.memo(Minus);
const MemoizedStar = React.memo(Star);
const MemoizedEye = React.memo(Eye);
const MemoizedScanEye = React.memo(ScanEye);

const MAX_PARALLEL_VERSION_PREVIEW_LOADS = 12;
const EMPTY_GENERATION_TASKS: GenerationTask[] = [];
let activeVersionPreviewLoads = 0;
interface VersionPreviewLoadQueueItem {
  cancelled: boolean;
  priority: number;
  sequence: number;
  run: () => void;
}
const versionPreviewLoadQueue: VersionPreviewLoadQueueItem[] = [];
let versionPreviewLoadSequence = 0;
const hydratedRejectedVersionPreviewIds = new Set<string>();
const VERSION_TREE_COLUMNS = 7;

function formatTaskElapsed(seconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

export const WorkspaceTaskProgress = React.memo(({ task }: { task: GenerationTask }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const startedAt = typeof task.startedAt === 'number' && Number.isFinite(task.startedAt)
    ? task.startedAt
    : null;
  const runtimeModelName = typeof task.model === 'string' ? task.model.trim() : '';
  const modelId = runtimeModelName ? resolveRegistryModelIdFromRuntimeName(runtimeModelName) : null;
  const modelDefinition = modelId && modelId in STUDIO_MODEL_REGISTRY
    ? STUDIO_MODEL_REGISTRY[modelId as StudioModelId]
    : null;
  const modelLabel = modelDefinition?.name || runtimeModelName || 'Studio model';
  const expectedSeconds = modelDefinition?.averageGenerationSeconds;
  const elapsedSeconds = startedAt === null ? null : Math.max(0, (nowMs - startedAt) / 1000);
  const progress = typeof task.loopProgress === 'number' && Number.isFinite(task.loopProgress)
    ? Math.min(1, Math.max(0, task.loopProgress > 1 ? task.loopProgress / 100 : task.loopProgress))
    : null;

  useEffect(() => {
    if (startedAt === null) return;
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [startedAt]);

  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-2 py-2 text-center">
      <div className="w-full truncate text-[10px] font-medium text-white/85" title={modelLabel}>
        {modelLabel}
      </div>
      {elapsedSeconds !== null && (
        <div className="font-mono text-[10px] tabular-nums text-white/55">
          {formatTaskElapsed(elapsedSeconds)}
          {expectedSeconds ? ` / ~${formatTaskElapsed(expectedSeconds)}` : ''}
        </div>
      )}
      <div
        className="absolute inset-x-2 bottom-2 h-1 overflow-hidden rounded-full bg-white/10"
        role="progressbar"
        aria-label={`Model task progress: ${modelLabel}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress === null ? undefined : Math.round(progress * 100)}
      >
        <div
          className={`h-full rounded-full bg-white/55 ${progress === null ? 'mkt-indeterminate w-1/3' : ''}`}
          style={progress === null ? undefined : { width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
});
WorkspaceTaskProgress.displayName = 'WorkspaceTaskProgress';

interface TreeVersionEntry {
  version: WorkspaceVersion;
  task?: GenerationTask;
  displayLabel?: string;
  row: number;
  column: number;
}

const pumpVersionPreviewQueue = () => {
  while (activeVersionPreviewLoads < MAX_PARALLEL_VERSION_PREVIEW_LOADS && versionPreviewLoadQueue.length > 0) {
    const nextTask = versionPreviewLoadQueue.shift();
    if (!nextTask) break;
    if (nextTask.cancelled) continue;
    nextTask.run();
  }
};

const enqueueVersionPreviewLoad = (url: string, priority = 0): { promise: Promise<HTMLImageElement>; cancel: () => void } => {
  const queueItem: VersionPreviewLoadQueueItem = {
    cancelled: false,
    priority,
    sequence: versionPreviewLoadSequence++,
    run: () => {},
  };

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    queueItem.run = () => {
      if (queueItem.cancelled) {
        reject(new Error(`Version preview load cancelled: ${url}`));
        return;
      }

      activeVersionPreviewLoads += 1;

      const image = new Image();
      image.crossOrigin = 'anonymous';

      image.onload = () => {
        activeVersionPreviewLoads = Math.max(0, activeVersionPreviewLoads - 1);
        if (queueItem.cancelled) {
          reject(new Error(`Version preview load cancelled: ${url}`));
        } else {
          resolve(image);
        }
        pumpVersionPreviewQueue();
      };

      image.onerror = () => {
        activeVersionPreviewLoads = Math.max(0, activeVersionPreviewLoads - 1);
        reject(new Error(`Failed to load version preview: ${url}`));
        pumpVersionPreviewQueue();
      };

      image.src = url;
    };
  });

  versionPreviewLoadQueue.push(queueItem);
  versionPreviewLoadQueue.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.sequence - b.sequence;
  });
  pumpVersionPreviewQueue();

  return {
    promise,
    cancel: () => {
      queueItem.cancelled = true;
    },
  };
};

interface WorkspaceVersionItemProps {
  version: WorkspaceVersion;
  task?: GenerationTask;
  displayLabel?: string;
  workspaceId: string;
  frameAspectRatio: number;
  isTask?: boolean;
  isActiveVersion?: boolean;
  isGoldenPath?: boolean;
  isVisible: boolean;
  preloadRejectedWaveToken?: number;
  previewCropRectNormalized?: {
    x: number;
    y: number;
    w: number;
    h: number;
  } | null;
  previewTopVisibleLayerId?: string | null;
}

interface VersionItemCanvasProps {
  imageUrl?: string;
  imageData?: ImageData | null;
  alt: string;
  loadPriority?: number;
  cropRectNormalized?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

// Never close() these: thumbnails that mount later draw the same bitmap, which is collected with its ImageData key.
const versionThumbnailBitmaps = new WeakMap<ImageData, Promise<ImageBitmap>>();

const getVersionThumbnailBitmap = (imageData: ImageData): Promise<ImageBitmap> => {
  let bitmap = versionThumbnailBitmaps.get(imageData);
  if (!bitmap) {
    const { width, height } = resolveVersionThumbnailSize(imageData.width, imageData.height);
    bitmap = createImageBitmap(imageData, { resizeWidth: width, resizeHeight: height, resizeQuality: 'high' });
    bitmap.catch(() => versionThumbnailBitmaps.delete(imageData));
    versionThumbnailBitmaps.set(imageData, bitmap);
  }
  return bitmap;
};

export const VersionItemCanvas = React.memo(({ imageUrl, imageData, alt, loadPriority = 0, cropRectNormalized }: VersionItemCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (imageData && imageData.width > 0 && imageData.height > 0) {
      if (Math.max(imageData.width, imageData.height) <= VERSION_THUMBNAIL_MAX_SIDE) {
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.putImageData(imageData, 0, 0);
        return;
      }

      let isBitmapCancelled = false;
      getVersionThumbnailBitmap(imageData)
        .then((bitmap) => {
          if (isBitmapCancelled) return;
          const thumbnailSize = resolveVersionThumbnailSize(imageData.width, imageData.height);
          canvas.width = thumbnailSize.width;
          canvas.height = thumbnailSize.height;
          ctx.drawImage(bitmap, 0, 0, thumbnailSize.width, thumbnailSize.height);
        })
        .catch((error) => {
          if (!isBitmapCancelled) {
            console.error('Failed to downscale the top layer for a version item.', error);
          }
        });

      return () => {
        isBitmapCancelled = true;
      };
    }

    if (!imageUrl) return;

    let isCancelled = false;
    const queuedLoad = enqueueVersionPreviewLoad(imageUrl, loadPriority);

    queuedLoad.promise
      .then((image) => {
        if (isCancelled) return;

        ctx.setTransform(1, 0, 0, 1, 0, 0);

        const hasNormalizedCrop =
          cropRectNormalized &&
          Number.isFinite(cropRectNormalized.x) &&
          Number.isFinite(cropRectNormalized.y) &&
          Number.isFinite(cropRectNormalized.w) &&
          Number.isFinite(cropRectNormalized.h) &&
          cropRectNormalized.w > 0 &&
          cropRectNormalized.h > 0;

        if (hasNormalizedCrop) {
          const normalizedRect = cropRectNormalized!;
          const sourceX = Math.max(0, Math.floor(normalizedRect.x * image.naturalWidth));
          const sourceY = Math.max(0, Math.floor(normalizedRect.y * image.naturalHeight));
          const sourceW = Math.max(1, Math.floor(normalizedRect.w * image.naturalWidth));
          const sourceH = Math.max(1, Math.floor(normalizedRect.h * image.naturalHeight));
          const boundedSourceW = Math.min(sourceW, image.naturalWidth - sourceX);
          const boundedSourceH = Math.min(sourceH, image.naturalHeight - sourceY);

          if (boundedSourceW > 0 && boundedSourceH > 0) {
            const croppedSize = resolveVersionThumbnailSize(boundedSourceW, boundedSourceH);
            canvas.width = croppedSize.width;
            canvas.height = croppedSize.height;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(
              image,
              sourceX,
              sourceY,
              boundedSourceW,
              boundedSourceH,
              0,
              0,
              croppedSize.width,
              croppedSize.height,
            );
            return;
          }
        }

        const imageSize = resolveVersionThumbnailSize(image.naturalWidth, image.naturalHeight);
        canvas.width = imageSize.width;
        canvas.height = imageSize.height;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);
      })
      .catch(() => {
        if (!isCancelled) {
          console.error(`Failed to load image for version item: ${imageUrl}`);
        }
      });

    return () => {
      isCancelled = true;
      queuedLoad.cancel();
    };
  }, [imageUrl, imageData, loadPriority, cropRectNormalized]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={alt}
      className="object-cover rounded-md w-full h-full"
    />
  );
});
VersionItemCanvas.displayName = 'VersionItemCanvas';

const WorkspaceVersionItem = React.memo(({ version, task, displayLabel, workspaceId, frameAspectRatio, isTask, isActiveVersion, isGoldenPath, isVisible, preloadRejectedWaveToken = 0, previewCropRectNormalized = null, previewTopVisibleLayerId = null }: WorkspaceVersionItemProps) => {
  const { kernel } = useKernel();
  const isRejectedVersion = !isTask && version.status === 'REJECTED';
  const [isRejectedPreviewHydrated, setIsRejectedPreviewHydrated] = useState<boolean>(() =>
    isRejectedVersion && hydratedRejectedVersionPreviewIds.has(version.id)
  );

  useEffect(() => {
    if (!isRejectedVersion) {
      return;
    }
    setIsRejectedPreviewHydrated(hydratedRejectedVersionPreviewIds.has(version.id));
  }, [isRejectedVersion, version.id]);

  const requestRejectedPreviewHydration = useCallback(() => {
    if (!isRejectedVersion || isRejectedPreviewHydrated) return;
    hydratedRejectedVersionPreviewIds.add(version.id);
    setIsRejectedPreviewHydrated(true);
  }, [isRejectedVersion, isRejectedPreviewHydrated, version.id]);

  useEffect(() => {
    if (!isRejectedVersion) return;
    if (preloadRejectedWaveToken <= 0) return;
    requestRejectedPreviewHydration();
  }, [isRejectedVersion, preloadRejectedWaveToken, requestRejectedPreviewHydration]);

  const handleClick = useCallback(() => {
    if (isTask) return;
    if (isRejectedVersion) {
      requestRejectedPreviewHydration();
    }
    if (kernel.current?.applyWorkspaceVersion) {
      kernel.current.applyWorkspaceVersion(workspaceId, version.id);
    }
  }, [kernel, workspaceId, version.id, isTask, isRejectedVersion, requestRejectedPreviewHydration]);

  const handlePromoteClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.promoteVersion) {
      kernel.current.promoteVersion(workspaceId, version.id);
    }
  }, [kernel, workspaceId, version.id]);

  const handleRejectClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.rejectWorkspaceVersions) {
      kernel.current.rejectWorkspaceVersions(workspaceId, [version.id]);
    }
  }, [kernel, workspaceId, version.id]);

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isTask) {
      if (!kernel.current || !task) return;

      const handleCopyDebugInfo = () => {
        try {
          const { layer, maskFile, layerContextSnapshot, ...debugInfo } = task;
          const debugString = JSON.stringify(debugInfo, null, 2);
          navigator.clipboard.writeText(debugString);
          toast.success('Debug info copied to clipboard.');
        } catch {
          kernel.current?.handleError?.('Failed to copy debug info.');
        }
        kernel.current?.setMenuData(null);
      };

      const removeAction = {
        label: 'Remove This Task',
        onClick: () => {
          const projectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
          if (!projectId) {
            kernel.current?.handleError?.('Cannot remove task: missing project scope.');
            kernel.current?.setMenuData(null);
            return;
          }
          kernel.current?.setGenerationTasks(projectId, prevTasks => prevTasks.filter(t => t.id !== task.id));
          kernel.current?.setMenuData(null);
        },
      };

      kernel.current?.setMenuData({
        position: { x: event.clientX, y: event.clientY },
        actions: [
          removeAction,
          { label: 'Copy Debug Info', onClick: handleCopyDebugInfo },
        ],
      });
      return;
    }

    if (!kernel.current) return;

    const actions = [];

    actions.push({
      label: 'Refresh Preview',
      onClick: () => {
        const currentKernel = kernel.current;
        if (!currentKernel?.regenerateVersionVisuals) {
          currentKernel?.handleError?.('Refresh action is unavailable.');
          return;
        }

        currentKernel.setMenuData(null);

        currentKernel
          .regenerateVersionVisuals(workspaceId, version.id)
          .then(() => {
            toast.success('Version preview refreshed.');
          })
          .catch(error => {
            console.error(`[WorkspaceCard] Failed to refresh version ${version.id}.`, error);
            currentKernel.handleError?.('Failed to refresh version preview.');
          });
      },
    });

    actions.push({
      label: version.favorite ? 'Unfavorite' : 'Favorite',
      onClick: () => {
        kernel.current?.toggleVersionFavorite(workspaceId, version.id);
        kernel.current?.setMenuData(null);
      },
    });

    if (version.status !== 'VALIDATED') {
      actions.push({
        label: 'Validate Version',
        onClick: () => {
          kernel.current?.promoteVersion(workspaceId, version.id);
          kernel.current?.setMenuData(null);
        },
      });
    }

    if (version.status === 'CANDIDATE') {
      actions.push({
        label: 'Reject Version',
        onClick: () => {
          kernel.current?.rejectWorkspaceVersions(workspaceId, [version.id]);
          kernel.current?.setMenuData(null);
        },
      });
    }

    if (version.status === 'CANDIDATE' || version.status === 'REJECTED' || version.status === 'ARCHIVED') {
      actions.push({
        label: 'Delete Version',
        onClick: () => {
          kernel.current?.deleteWorkspaceVersion(workspaceId, version.id);
          kernel.current?.setMenuData(null);
        },
      });
    }

    if (actions.length > 0) {
      kernel.current?.setMenuData({
        position: { x: event.clientX, y: event.clientY },
        actions: actions,
      });
    }
  }, [kernel, workspaceId, version.id, version.status, isTask, task, version.favorite]);

  const itemStyle = useMemo(() => ({
    width: '100%',
    height: 'auto',
    aspectRatio: frameAspectRatio,
    contain: 'layout',
    overflow: 'visible',
  }), [frameAspectRatio]);

  const borderClasses = useMemo(() => {
    if (isTask) return 'border-gray-700';
    if (isActiveVersion) return 'border-[#B3D9FF]';
    if (isGoldenPath) return 'border-gray-500 hover:border-gray-400';

    switch (version.status) {
      case 'VALIDATED':
      case 'CANDIDATE':
        return 'border-gray-600 hover:border-gray-800';
      case 'REJECTED':
        return 'border-gray-800';
      case 'ARCHIVED':
        return 'border-gray-700';
      default:
        return 'border-gray-600';
    }
  }, [isTask, isActiveVersion, isGoldenPath, version.status]);

  const opacityClass = '';
  const displayImageUrl = version.imageUrl;
  const canvasAlt = version.name;
  const essentialAssetsReady = useKernelStore((state) => {
    const workspaceDto = state.inventory.StudioWorkspace.registry[workspaceId];
    const projectId = workspaceDto?.projectId?.trim();
    if (!projectId) return false;
    return state.inventory.Project.registry[projectId]?.essentialAssetsReady === true;
  });
  const loadedTopLayerImageData = useKernelStore((state) => {
    if (!previewTopVisibleLayerId || isTask) return null;
    return state.inventory.StudioLayer.registry[previewTopVisibleLayerId]?.imageData ?? null;
  });
  const shouldUseLoadedTopLayerPreview =
    !isTask &&
    !!previewTopVisibleLayerId &&
    !!loadedTopLayerImageData &&
    loadedTopLayerImageData.width > 0 &&
    loadedTopLayerImageData.height > 0;
  const shouldLoadPreviewFromUrl = essentialAssetsReady && !!displayImageUrl && (!isRejectedVersion || isRejectedPreviewHydrated);
  const shouldLoadPreview = shouldUseLoadedTopLayerPreview || shouldLoadPreviewFromUrl;
  const shouldShowLoadingAnimation = !isRejectedVersion || isRejectedPreviewHydrated;
  const previewLoadPriority = useMemo(() => {
    const timestampPriority = Number.isFinite(version.timestamp) ? version.timestamp : 0;
    const activeBoost = isActiveVersion ? 2_000_000_000_000_000 : 0;
    const visibleBoost = isVisible ? 1_000_000_000_000_000 : 0;
    const rejectedPenalty = isRejectedVersion ? 3_000_000_000_000_000 : 0;
    return timestampPriority + activeBoost + visibleBoost - rejectedPenalty;
  }, [version.timestamp, isActiveVersion, isVisible, isRejectedVersion]);
  const activeCardHaloStyle = useMemo(() => ({
    boxShadow: '0 0 0 1px rgba(179, 217, 255, 0.52), 0 0 12px rgba(179, 217, 255, 0.48), 0 0 20px rgba(179, 217, 255, 0.25)',
    zIndex: 0,
  }), []);

  return (
    <div
      className={`workspace-version-item border rounded-md relative flex items-center justify-center transition-all ${isTask ? 'cursor-default' : 'cursor-pointer'} ${borderClasses} ${opacityClass} ${isVisible ? '' : 'hidden'}`}
      onClick={handleClick}
      onMouseEnter={requestRejectedPreviewHydration}
      onContextMenu={handleContextMenu}
      style={itemStyle}
    >
      {isActiveVersion && !isTask && (
        <div
          className="absolute -inset-[3px] rounded-[9px] border border-[#B3D9FF]/90 animate-pulse pointer-events-none"
          style={activeCardHaloStyle}
        />
      )}
      {version.status === 'VALIDATED' && !isTask && (
        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-neutral-900 border border-[#B3D9FF] rounded-full flex items-center justify-center pointer-events-none" style={{ zIndex: 1 }}>
          <MemoizedCheck className="w-2 h-2 text-[#B3D9FF]" strokeWidth={3} />
        </div>
      )}
      {version.favorite && version.status !== 'VALIDATED' && version.status !== 'CANDIDATE' && !isTask && (
        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-neutral-900 border border-yellow-500 rounded-full flex items-center justify-center pointer-events-none" style={{ zIndex: 1 }}>
          <MemoizedStar className="w-2 h-2 text-yellow-500 fill-yellow-500" strokeWidth={1} />
        </div>
      )}
      {!version.favorite && version.status === 'REJECTED' && !isTask && (
        <div
          title="Version rejected"
          className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-neutral-900 border border-red-500 rounded-full flex items-center justify-center pointer-events-none"
          style={{ zIndex: 1 }}
        >
          <MemoizedThumbsDown className="w-2 h-2 text-red-500" strokeWidth={2.5} />
        </div>
      )}
      {version.status === 'CANDIDATE' && !isTask && (
        <div
          onClick={handlePromoteClick}
          title="Set as validated version"
          className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-neutral-800 border border-neutral-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-emerald-900 hover:border-emerald-400 transition-all"
          style={{ zIndex: 1 }}
        >
          <MemoizedThumbsUp className="w-2 h-2 text-gray-400" strokeWidth={2.5} />
        </div>
      )}
      {version.status === 'CANDIDATE' && !isTask && (
        <div
          onClick={handleRejectClick}
          title="Reject Version"
          className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-neutral-800 border border-neutral-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-900 hover:border-red-500 transition-all"
          style={{ zIndex: 1 }}
        >
          <MemoizedThumbsDown className="w-2 h-2 text-gray-400" strokeWidth={2.5} />
        </div>
      )}
      {!isTask && typeof displayLabel === 'string' && displayLabel.length > 0 && (
        <div
          className="absolute -top-2.5 left-1/2 -translate-x-1/2 font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          style={{ color: isActiveVersion ? '#B3D9FF' : 'rgba(209, 213, 219, 0.7)', fontSize: '8px' }}
        >
          {displayLabel}
        </div>
      )}
      {shouldLoadPreview ? (
        <VersionItemCanvas
          imageUrl={shouldLoadPreviewFromUrl ? displayImageUrl : undefined}
          imageData={shouldUseLoadedTopLayerPreview ? loadedTopLayerImageData : undefined}
          alt={canvasAlt}
          loadPriority={previewLoadPriority}
          cropRectNormalized={
            !isTask && !shouldUseLoadedTopLayerPreview
              ? (previewCropRectNormalized ?? undefined)
              : undefined
          }
        />
      ) : (
        <div className={`absolute inset-0 rounded-md ${shouldShowLoadingAnimation ? 'loading-anim-bg' : 'bg-neutral-900/60'}`}></div>
      )}
      {isTask && task && <WorkspaceTaskProgress task={task} />}
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.isTask !== nextProps.isTask) return false;

  if (prevProps.isTask) {
    if (
      prevProps.task?.id !== nextProps.task?.id ||
      prevProps.task?.model !== nextProps.task?.model ||
      prevProps.task?.startedAt !== nextProps.task?.startedAt ||
      prevProps.task?.loopProgress !== nextProps.task?.loopProgress
    ) return false;
  } else if (
    prevProps.version.id !== nextProps.version.id ||
    prevProps.version.imageUrl !== nextProps.version.imageUrl ||
    prevProps.version.name !== nextProps.version.name ||
    prevProps.version.timestamp !== nextProps.version.timestamp ||
    prevProps.version.status !== nextProps.version.status ||
    prevProps.version.favorite !== nextProps.version.favorite
  ) {
    return false;
  }

  return (
    prevProps.displayLabel === nextProps.displayLabel &&
    prevProps.workspaceId === nextProps.workspaceId &&
    prevProps.frameAspectRatio === nextProps.frameAspectRatio &&
    prevProps.isActiveVersion === nextProps.isActiveVersion &&
    prevProps.isGoldenPath === nextProps.isGoldenPath &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.preloadRejectedWaveToken === nextProps.preloadRejectedWaveToken &&
    (prevProps.previewTopVisibleLayerId ?? null) === (nextProps.previewTopVisibleLayerId ?? null) &&
    (prevProps.previewCropRectNormalized?.x ?? null) === (nextProps.previewCropRectNormalized?.x ?? null) &&
    (prevProps.previewCropRectNormalized?.y ?? null) === (nextProps.previewCropRectNormalized?.y ?? null) &&
    (prevProps.previewCropRectNormalized?.w ?? null) === (nextProps.previewCropRectNormalized?.w ?? null) &&
    (prevProps.previewCropRectNormalized?.h ?? null) === (nextProps.previewCropRectNormalized?.h ?? null)
  );
});
WorkspaceVersionItem.displayName = 'WorkspaceVersionItem';

interface WorkspaceTaskItemProps {
  task: GenerationTask;
  workspaceId: string;
  frameAspectRatio: number;
}

const WorkspaceTaskItem = React.memo(({ task, workspaceId, frameAspectRatio }: WorkspaceTaskItemProps) => {
  const versionForTask: WorkspaceVersion = useMemo(() => ({
    id: task.id,
    name: task.params?.prompt || `Task: ${task.type}`,
    timestamp: 0,
    layerVisibility: [],
    imageUrl: undefined,
    status: 'CANDIDATE',
    parentId: null,
  }), [task.id, task.params?.prompt, task.type]);

  return (
    <WorkspaceVersionItem
      version={versionForTask}
      task={task}
      workspaceId={workspaceId}
      frameAspectRatio={frameAspectRatio}
      isVisible={true}
      isTask={true}
    />
  );
});
WorkspaceTaskItem.displayName = 'WorkspaceTaskItem';

interface WorkspaceVersionsPanelProps {
  projectId: string;
  workspace: Workspace;
  isActive: boolean;
  renderVersionStrip?: boolean;
}

const DEFAULT_PAN_OFFSET = { x: 0, y: 0 } as const;

const WorkspaceVersionsPanel = React.memo(({
  projectId,
  workspace,
  isActive,
  renderVersionStrip = true,
}: WorkspaceVersionsPanelProps) => {
  const { kernel } = useKernel();

  const generationTasks = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return Array.isArray(runtime?.generationTasks) ? runtime.generationTasks : EMPTY_GENERATION_TASKS;
  });
  const zoomLevel = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const panOffset = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.panOffset;
    if (
      value &&
      typeof value === 'object' &&
      Number.isFinite((value as { x?: unknown }).x) &&
      Number.isFinite((value as { y?: unknown }).y)
    ) {
      return value as { x: number; y: number };
    }
    return DEFAULT_PAN_OFFSET;
  });
  const versionsPerRow = VERSION_TREE_COLUMNS;
  const useFocusedVersionPreview = useKernelStore((state) => state.userPreferences.workspaceVersionPreviewFocusTopVisibleLayerRect === true);
  const layerRegistry = useKernelStore((state) => state.inventory.StudioLayer.registry);
  const essentialAssetsReady = useKernelStore((state) => {
    const workspaceDto = state.inventory.StudioWorkspace.registry[workspace.id];
    const resolvedProjectId = workspaceDto?.projectId?.trim();
    if (!resolvedProjectId) return false;
    return state.inventory.Project.registry[resolvedProjectId]?.essentialAssetsReady === true;
  });
  const [preloadRejectedWaveToken, setPreloadRejectedWaveToken] = useState(0);

  const versionFilterMode = workspace.versionFilterMode || 'PRIMARY';
  const allWorkspaceVersions = workspace.versions || [];
  const fallbackDisplayLabelById = useMemo(() => {
    const map = new Map<string, string>();
    allWorkspaceVersions.forEach((version, index) => {
      map.set(version.id, String(index + 1));
    });
    return map;
  }, [allWorkspaceVersions]);

  const workspaceTasks = useMemo(() => {
    return generationTasks.filter(task => task.workspaceId === workspace.id);
  }, [generationTasks, workspace.id]);

  const activeTasks = useMemo(() => {
    return workspaceTasks.filter(task => !task.isOutcomeReported && !task.error);
  }, [workspaceTasks]);

  const activeTaskCount = activeTasks.length;

  const activeVersionId = useMemo(() => {
    return workspace.activeVersionId ?? null;
  }, [workspace.activeVersionId]);
  const treeFocusVersionId = useMemo(() => {
    const validatedId = typeof workspace.validatedVersionId === 'string'
      ? workspace.validatedVersionId.trim()
      : '';
    if (validatedId && allWorkspaceVersions.some((version) => version.id === validatedId)) {
      return validatedId;
    }

    const oldestVersion = allWorkspaceVersions
      .slice()
      .sort(sortVersionsByTimeline)[0];
    return oldestVersion?.id ?? null;
  }, [workspace.validatedVersionId, allWorkspaceVersions]);
  const versionDisplayLabelById = useMemo(() => {
    const inferredLabels = computeVersionHierarchyLabels(allWorkspaceVersions, treeFocusVersionId);
    if (inferredLabels.size > 0) {
      return inferredLabels;
    }
    return fallbackDisplayLabelById;
  }, [allWorkspaceVersions, treeFocusVersionId, fallbackDisplayLabelById]);
  const goldenPathVersionIds = useMemo(() => {
    return new Set(
      computeLineageFromFocus(allWorkspaceVersions, treeFocusVersionId).map((version) => version.id)
    );
  }, [allWorkspaceVersions, treeFocusVersionId]);

  const visibleVersionIds = useMemo(() => {
    const allVersions = allWorkspaceVersions;
    if (!allVersions.length) {
      return new Set<string>();
    }

    if (versionFilterMode === 'ALL') {
      return new Set(allVersions.map(v => v.id));
    }

    if (versionFilterMode === 'PRIMARY') {
      const focusVersion = treeFocusVersionId
        ? allVersions.find((version) => version.id === treeFocusVersionId) ?? null
        : null;

      if (!focusVersion) {
        return new Set<string>();
      }

      const visibleIds = new Set<string>();
      visibleIds.add(focusVersion.id);

      const childrenMap = new Map<string, WorkspaceVersion[]>();
      const versionById = new Map<string, WorkspaceVersion>();
      allVersions.forEach((version) => {
        versionById.set(version.id, version);
        if (!version.parentId) return;
        const children = childrenMap.get(version.parentId) || [];
        children.push(version);
        childrenMap.set(version.parentId, children);
      });

      const queue: string[] = [focusVersion.id];
      while (queue.length > 0) {
        const cursorId = queue.shift();
        if (!cursorId) continue;
        const children = childrenMap.get(cursorId) || [];
        children.forEach((child) => {
          if (visibleIds.has(child.id)) return;
          visibleIds.add(child.id);
          queue.push(child.id);
        });
      }

      // Primary mode should surface validated + meaningful candidate/rejected outcomes,
      // while hiding archived intermediary checkpoints created during parallel launch churn.
      const filteredVisibleIds = new Set<string>();
      visibleIds.forEach((versionId) => {
        const version = versionById.get(versionId);
        if (!version) return;
        if (version.id === focusVersion.id || version.status !== 'ARCHIVED') {
          filteredVisibleIds.add(versionId);
        }
      });

      return filteredVisibleIds;
    }
    return new Set<string>();
  }, [allWorkspaceVersions, versionFilterMode, treeFocusVersionId]);
  const visibleVersions = useMemo(() => {
    return allWorkspaceVersions.filter((version) => visibleVersionIds.has(version.id));
  }, [allWorkspaceVersions, visibleVersionIds]);
  const { previewCropRectByVersionId, previewTopVisibleLayerIdByVersionId } = useMemo(() => {
    const cropMap = new Map<string, { x: number; y: number; w: number; h: number }>();
    const topLayerIdMap = new Map<string, string>();
    if (!useFocusedVersionPreview) {
      return {
        previewCropRectByVersionId: cropMap,
        previewTopVisibleLayerIdByVersionId: topLayerIdMap,
      };
    }

    const frameWidth = workspace.frameRect.w;
    const frameHeight = workspace.frameRect.h;
    if (!(frameWidth > 0) || !(frameHeight > 0)) {
      return {
        previewCropRectByVersionId: cropMap,
        previewTopVisibleLayerIdByVersionId: topLayerIdMap,
      };
    }

    const clamp = (value: number, min: number, max: number): number =>
      Math.min(max, Math.max(min, value));

    allWorkspaceVersions.forEach((version) => {
      const visibleRenderableLayers = version.layerVisibility
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => entry.visible)
        .map(({ entry, index }) => {
          const layer = layerRegistry[entry.id];
          if (!layer || layer.type === 'video' || layer.type === 'audio') return null;
          // Exclude "Always Above" layers from focused version preview selection.
          // They are compositing helpers and should not drive the card crop target.
          const isPinnedTopLayer =
            layer.topLayer === true ||
            Reflect.get(layer as object, 'top-layer') === true;
          if (isPinnedTopLayer) return null;
          return { layer, index };
        })
        .filter((candidate): candidate is { layer: NonNullable<typeof candidate>['layer']; index: number } => Boolean(candidate));

      if (visibleRenderableLayers.length === 0) return;

      visibleRenderableLayers.sort((left, right) => {
        const leftOrder = Number.isFinite(left.layer.renderOrder) ? (left.layer.renderOrder as number) : left.index;
        const rightOrder = Number.isFinite(right.layer.renderOrder) ? (right.layer.renderOrder as number) : right.index;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.index - right.index;
      });

      const topVisibleLayer = visibleRenderableLayers[visibleRenderableLayers.length - 1]?.layer;
      if (!topVisibleLayer) return;
      topLayerIdMap.set(version.id, topVisibleLayer.id);

      const rawX = Number.isFinite(topVisibleLayer.rect?.x) ? topVisibleLayer.rect.x : 0;
      const rawY = Number.isFinite(topVisibleLayer.rect?.y) ? topVisibleLayer.rect.y : 0;
      const rawW = Number.isFinite(topVisibleLayer.rect?.w) ? topVisibleLayer.rect.w : 0;
      const rawH = Number.isFinite(topVisibleLayer.rect?.h) ? topVisibleLayer.rect.h : 0;
      if (!(rawW > 0) || !(rawH > 0)) return;

      const boundedX1 = clamp(rawX, 0, frameWidth);
      const boundedY1 = clamp(rawY, 0, frameHeight);
      const boundedX2 = clamp(rawX + rawW, 0, frameWidth);
      const boundedY2 = clamp(rawY + rawH, 0, frameHeight);
      const boundedW = boundedX2 - boundedX1;
      const boundedH = boundedY2 - boundedY1;
      if (!(boundedW > 0) || !(boundedH > 0)) return;

      cropMap.set(version.id, {
        x: boundedX1 / frameWidth,
        y: boundedY1 / frameHeight,
        w: boundedW / frameWidth,
        h: boundedH / frameHeight,
      });
    });

    return {
      previewCropRectByVersionId: cropMap,
      previewTopVisibleLayerIdByVersionId: topLayerIdMap,
    };
  }, [useFocusedVersionPreview, allWorkspaceVersions, layerRegistry, workspace.frameRect.w, workspace.frameRect.h]);

  const toggleVersionFilter = useCallback(() => {
    if (!kernel.current) return;
    const newMode = versionFilterMode === 'PRIMARY' ? 'ALL' : 'PRIMARY';
    kernel.current.setWorkspace(workspace.id, (currentWorkspace) => ({
      ...currentWorkspace,
      versionFilterMode: newMode,
    }), projectId);
  }, [kernel, workspace.id, versionFilterMode, projectId]);
  const toggleFocusedVersionPreviewPreference = useCallback(() => {
    if (!kernel.current?.updateAndSaveUserPreference) return;
    void kernel.current.updateAndSaveUserPreference(
      'workspaceVersionPreviewFocusTopVisibleLayerRect',
      !useFocusedVersionPreview,
    );
  }, [kernel, useFocusedVersionPreview]);

  const frameAspectRatio = useMemo(() => {
    if (!workspace.frameRect || workspace.frameRect.h === 0) return 1;
    return workspace.frameRect.w / workspace.frameRect.h;
  }, [workspace.frameRect]);

  const versionFilterButtonSize = 18;
  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  const [hasBeenActive, setHasBeenActive] = useState<boolean>(isActive);
  useEffect(() => {
    if (isActive) {
      setHasBeenActive(true);
    }
  }, [isActive]);

  const shouldMountVersionStrip =
    renderVersionStrip &&
    hasBeenActive &&
    (allWorkspaceVersions.length > 0 || activeTasks.length > 0) &&
    (workspace.visible ?? true);
  const shouldRenderVersionStrip = shouldMountVersionStrip && isActive;
  useEffect(() => {
    if (!shouldRenderVersionStrip) return;
    if (!essentialAssetsReady) return;
    if (!visibleVersions.some((version) => version.status === 'REJECTED')) return;

    const timer = window.setTimeout(() => {
      setPreloadRejectedWaveToken((current) => current + 1);
    }, 120);

    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldRenderVersionStrip, essentialAssetsReady, visibleVersions, workspace.id, versionFilterMode]);

  const treeTasks = useMemo(() => {
    return activeTasks.filter((task) => {
      const parentVersionId = typeof task.parentVersionId === 'string' ? task.parentVersionId.trim() : '';
      return parentVersionId.length > 0 && visibleVersionIds.has(parentVersionId);
    });
  }, [activeTasks, visibleVersionIds]);

  const treeLayoutData = useMemo(() => {
    if (visibleVersions.length === 0) {
      return {
        entries: [] as TreeVersionEntry[],
        columnCount: 1,
        rowCount: 1,
      };
    }

    const taskByTreeNodeId = new Map<string, GenerationTask>();
    const treeVersions: WorkspaceVersion[] = [...visibleVersions];
    const taskTimestampBase = Number.MAX_SAFE_INTEGER - Math.max(1, treeTasks.length + 1);
    treeTasks.forEach((task, index) => {
      const parentVersionId = typeof task.parentVersionId === 'string' ? task.parentVersionId.trim() : '';
      if (!parentVersionId) return;

      const treeTaskNodeId = `task:${task.id}`;
      taskByTreeNodeId.set(treeTaskNodeId, task);
      const taskLayer = task.layer;
      const taskPreviewImageUrl = taskLayer?.type === 'video'
        ? taskLayer.videoFramesUrls?.find((url) => typeof url === 'string' && url.trim().length > 0)
        : taskLayer?.imageSrc;

      treeVersions.push({
        id: treeTaskNodeId,
        name: task.params?.prompt || `Task: ${task.type}`,
        timestamp: taskTimestampBase + index,
        layerVisibility: [],
        imageUrl: taskPreviewImageUrl,
        status: 'CANDIDATE',
        parentId: parentVersionId,
      });
    });

    const versionMap = new Map<string, WorkspaceVersion>();
    treeVersions.forEach((version) => {
      versionMap.set(version.id, version);
    });
    const focusVersion =
      (treeFocusVersionId ? versionMap.get(treeFocusVersionId) : null) ??
      visibleVersions.slice().sort(sortVersionsByRecency)[0] ??
      null;

    if (!focusVersion) {
      return {
        entries: [] as TreeVersionEntry[],
        columnCount: Math.max(2, versionsPerRow),
        rowCount: 1,
      };
    }

    // Left column: focus -> ancestors. Row 0 is newest/focus, older ancestors go downward.
    const lineageEntries = computeLineageFromFocus(visibleVersions, focusVersion.id);
    const lineageIds = new Set<string>(lineageEntries.map((version) => version.id));

    const lineageIndexById = new Map<string, number>();
    lineageEntries.forEach((version, lineageIndex) => {
      lineageIndexById.set(version.id, lineageIndex);
    });

    const resolveAnchorLineageIndex = (version: WorkspaceVersion): number => {
      let cursorId: string | null = version.id;
      const visited = new Set<string>();

      while (cursorId && !visited.has(cursorId)) {
        visited.add(cursorId);
        const lineageIndex = lineageIndexById.get(cursorId);
        if (typeof lineageIndex === 'number') {
          return lineageIndex;
        }
        const parentId: string | null = versionMap.get(cursorId)?.parentId ?? null;
        cursorId =
          typeof parentId === 'string' && parentId.trim().length > 0 && versionMap.has(parentId)
            ? parentId
            : null;
      }

      return 0;
    };

    const offshootsByLineageIndex = new Map<number, WorkspaceVersion[]>();
    treeVersions
      .filter((version) => !lineageIds.has(version.id))
      .forEach((version) => {
        const anchorIndex = resolveAnchorLineageIndex(version);
        const bucket = offshootsByLineageIndex.get(anchorIndex) || [];
        bucket.push(version);
        offshootsByLineageIndex.set(anchorIndex, bucket);
      });
    offshootsByLineageIndex.forEach((bucket, anchorIndex) => {
      bucket.sort(sortVersionsByTimeline);
      offshootsByLineageIndex.set(anchorIndex, bucket);
    });

    const columnCount = Math.max(2, versionsPerRow);
    const rightColumnCount = Math.max(1, columnCount - 1);
    const entries: TreeVersionEntry[] = [];
    const placedNodeIds = new Set<string>();
    const anchorRowByLineageIndex = new Map<number, number>();
    let nextBlockStartRow = 0;

    lineageEntries.forEach((_, lineageIndex) => {
      const bucketSize = (offshootsByLineageIndex.get(lineageIndex) || []).length;
      const blockHeight = Math.max(1, Math.ceil(bucketSize / rightColumnCount));
      const anchorRow = nextBlockStartRow + (blockHeight - 1);
      anchorRowByLineageIndex.set(lineageIndex, anchorRow);
      nextBlockStartRow += blockHeight;
    });

    lineageEntries.forEach((version, lineageIndex) => {
      const anchorRow = anchorRowByLineageIndex.get(lineageIndex) ?? lineageIndex;
      placedNodeIds.add(version.id);
      entries.push({
        version,
        displayLabel: versionDisplayLabelById.get(version.id) ?? String(entries.length + 1),
        row: anchorRow,
        column: 0,
      });

      const bucket = (offshootsByLineageIndex.get(lineageIndex) || [])
        .filter((offshootVersion) => !placedNodeIds.has(offshootVersion.id));

      bucket.forEach((offshootVersion, index) => {
        if (placedNodeIds.has(offshootVersion.id)) return;
        placedNodeIds.add(offshootVersion.id);
        entries.push({
          version: offshootVersion,
          task: taskByTreeNodeId.get(offshootVersion.id),
          displayLabel: taskByTreeNodeId.has(offshootVersion.id)
            ? undefined
            : (versionDisplayLabelById.get(offshootVersion.id) ?? String(entries.length + 1)),
          row: anchorRow - Math.floor(index / rightColumnCount),
          column: (index % rightColumnCount) + 1,
        });
      });
    });

    // Safety net for malformed histories: keep any remaining nodes visible and ordered.
    const leftovers = treeVersions
      .filter((version) => !placedNodeIds.has(version.id))
      .sort(sortVersionsByTimeline);
    if (leftovers.length > 0) {
      const currentMaxRow = entries.reduce((max, entry) => Math.max(max, entry.row), 0);
      leftovers.forEach((version, index) => {
        placedNodeIds.add(version.id);
        entries.push({
          version,
          task: taskByTreeNodeId.get(version.id),
          displayLabel: taskByTreeNodeId.has(version.id)
            ? undefined
            : (versionDisplayLabelById.get(version.id) ?? String(entries.length + 1)),
          row: currentMaxRow + 1 + Math.floor(index / rightColumnCount),
          column: (index % rightColumnCount) + 1,
        });
      });
    }

    const rowCount = Math.max(
      1,
      ...entries.map((entry) => entry.row + 1),
      lineageEntries.length,
    );

    return {
      entries,
      columnCount,
      rowCount,
    };
  }, [visibleVersions, versionDisplayLabelById, treeFocusVersionId, versionsPerRow, treeTasks]);
  const panelContent = (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {shouldMountVersionStrip && (
        <div
          id={isActive ? 'editor-versions-strip' : undefined}
          className="version-strip-container"
          style={{
            position: 'absolute',
            left: buildLiveScreenX(workspace.frameRect.x, panOffset.x, zoomLevel),
            top: `calc(${buildLiveScreenY(workspace.frameRect.y + workspace.frameRect.h + 8, panOffset.y, zoomLevel)} + 8px)`,
            width: buildLiveScreenSize(workspace.frameRect.w, zoomLevel),
            display: isActive ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            pointerEvents: isActive ? 'auto' : 'none',
            zIndex: 1,
            userSelect: 'none',
          }}
        >
          <>
            {workspace.versions && workspace.versions.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: `calc(-${versionFilterButtonSize * 2}px - 16px)`,
                  top: '2px',
                  width: `${versionFilterButtonSize}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ...STUDIO_LIVE_VIEWPORT_VARS_RESET,
                }}
              >
                <button
                  onClick={toggleFocusedVersionPreviewPreference}
                  className={`relative flex items-center justify-center text-white rounded-full backdrop-blur-sm border transition-colors ${
                    useFocusedVersionPreview
                      ? 'bg-[#B3D9FF]/20 border-[#B3D9FF]/80 hover:bg-[#B3D9FF]/30'
                      : 'bg-neutral-800/60 border-neutral-700 hover:bg-neutral-700/80'
                  }`}
                  style={{
                    width: `${versionFilterButtonSize}px`,
                    height: `${versionFilterButtonSize}px`,
                  }}
                  title={useFocusedVersionPreview ? 'Version preview mode: top-layer focus' : 'Version preview mode: full frame'}
                  aria-label={useFocusedVersionPreview ? 'Version preview mode: top-layer focus' : 'Version preview mode: full frame'}
                >
                  {useFocusedVersionPreview ? (
                    <MemoizedScanEye
                      size={versionFilterButtonSize - 10}
                      strokeWidth={2.6}
                      className={useFocusedVersionPreview ? 'text-[#B3D9FF]' : 'text-neutral-400'}
                    />
                  ) : (
                    <MemoizedEye
                      size={versionFilterButtonSize - 10}
                      strokeWidth={2.6}
                      className={useFocusedVersionPreview ? 'text-[#B3D9FF]' : 'text-neutral-400'}
                    />
                  )}
                </button>
              </div>
            )}
            {workspace.versions && workspace.versions.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: `calc(-${versionFilterButtonSize}px - 8px)`,
                  top: '2px',
                  width: `${versionFilterButtonSize}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ...STUDIO_LIVE_VIEWPORT_VARS_RESET,
                }}
              >
                <button
                  onClick={toggleVersionFilter}
                  className="relative flex items-center justify-center bg-neutral-800/60 text-white rounded-full backdrop-blur-sm border border-neutral-700 hover:bg-neutral-700/80 transition-colors"
                  style={{
                    width: `${versionFilterButtonSize}px`,
                    height: `${versionFilterButtonSize}px`,
                  }}
                  title={versionFilterMode === 'PRIMARY' ? 'Show All Versions' : 'Show Primary Versions'}
                  aria-label={versionFilterMode === 'PRIMARY' ? 'Show All Versions' : 'Show Primary Versions'}
                >
                  {versionFilterMode === 'PRIMARY' ? (
                    <MemoizedPlus
                      size={versionFilterButtonSize - 10}
                      strokeWidth={3}
                      className="text-neutral-400"
                    />
                  ) : (
                    <MemoizedMinus
                      size={versionFilterButtonSize - 10}
                      strokeWidth={3}
                      className="text-neutral-400"
                    />
                  )}
                </button>
              </div>
            )}
            {treeLayoutData.entries.length > 0 && (
              <div
                className="version-tree group"
                style={{
                  position: 'relative',
                  width: '100%',
                  ...STUDIO_LIVE_VIEWPORT_VARS_RESET,
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${treeLayoutData.columnCount}, minmax(0, 1fr))`,
                    gridTemplateRows: `repeat(${treeLayoutData.rowCount}, minmax(0, auto))`,
                    gap: '0.5rem',
                    width: '100%',
                  }}
                >
                  {treeLayoutData.entries.map((entry) => (
                    <div
                      key={`tree-version-${entry.version.id}`}
                      style={{
                        gridColumnStart: entry.column + 1,
                        gridRowStart: entry.row + 1,
                        minWidth: 0,
                      }}
                    >
                      {entry.task ? (
                        <WorkspaceTaskItem
                          task={entry.task}
                          workspaceId={workspace.id}
                          frameAspectRatio={frameAspectRatio}
                        />
                      ) : (
                        <WorkspaceVersionItem
                          version={entry.version}
                          displayLabel={entry.displayLabel}
                          workspaceId={workspace.id}
                          frameAspectRatio={frameAspectRatio}
                          isActiveVersion={entry.version.id === activeVersionId}
                          isGoldenPath={goldenPathVersionIds.has(entry.version.id)}
                          isVisible={true}
                          preloadRejectedWaveToken={preloadRejectedWaveToken}
                          previewCropRectNormalized={previewCropRectByVersionId.get(entry.version.id) ?? null}
                          previewTopVisibleLayerId={previewTopVisibleLayerIdByVersionId.get(entry.version.id) ?? null}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        </div>
      )}
    </div>
  );

  return portalHost ? createPortal(panelContent, portalHost) : panelContent;
});

WorkspaceVersionsPanel.displayName = 'WorkspaceVersionsPanel';

export default WorkspaceVersionsPanel;
